import { LoadingButton } from '@mui/lab';
import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete as IAutocomplete,
  Chip,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Transition } from '../../components/Transition';
import { LANGUAGE, PUSH_NOTIFICATION_TYPE } from '../../constants';
import { PushNotificationType } from '../../enum';
import {
  findSelectCategory,
  searchSelectCategory,
} from '../../store/selectCategory/api';
import {
  reset as resetCategory,
  setSkip as setSkipCategory,
} from '../../store/selectCategory/selectCategorySlice';
import { useAppDispatch, useAppSelector } from '../../store';
import { getQatarDatetimeLocalMin } from '../../utils/qatarTime';
import { useEffect, useState } from 'react';
import { uniqBy } from 'lodash';
import AutoComplete from '../../components/AutoComplete';
import { NumericFormatCustom } from '../../components/NumericCustomFormatter';
import {
  createPushNotificationManagement,
  // findPushNotificationManagement,
} from '../../store/pushNotificationManagement/api';
import { reset as resetPushNotificationManagement } from '../../store/pushNotificationManagement/pushNotificationManagementSlice';
import { isValidArray } from '../../utils/valid';

interface IProps {
  toggle: boolean;
  onClose: () => void;
  values: any;
  handleSubmit: () => void;
  setFieldValue: (key: string, value: any) => void;
  handleChange: (e: React.ChangeEvent<any>) => void;
  errors: any;
  touched: any;
  isSubmitting: boolean;
  resetForm: () => void;
}

const NotificationFormModal: React.FC<IProps> = ({
  toggle,
  onClose,
  errors,
  touched,
  values,
  isSubmitting,
  setFieldValue,
  handleChange,
  handleSubmit,
  resetForm,
}) => {
  const dispatch = useAppDispatch();
  const { count } = useAppSelector((state) => state.pushNotificationManagement);
  const [inputValue, setInputValue] = useState<string>('');
  const [chips, setChips] = useState<string[]>([]);

  const handleGetCountUser = async () => {
    if (values?.type) {
      if (values?.type === PushNotificationType.address) {
        await dispatch(
          createPushNotificationManagement({
            type: values.type,
            countOnly: true,
            ...(values.zone && { zone: values?.zone }),
          })
        ).unwrap();
      }
      if (values?.type === PushNotificationType.category) {
        await dispatch(
          createPushNotificationManagement({
            type: values.type,
            countOnly: true,
            ...(values.categoryId && {
              categoryId: values?.categoryId,
            }),
          })
        ).unwrap();
      }
      if (values?.type === PushNotificationType.language) {
        await dispatch(
          createPushNotificationManagement({
            type: values.type,
            countOnly: true,
            ...(values.language && {
              language: values?.language,
            }),
          })
        ).unwrap();
      }
      if (values?.type === PushNotificationType.orderAmount) {
        await dispatch(
          createPushNotificationManagement({
            type: values.type,
            countOnly: true,
            ...(values.minOrderAmount && {
              minOrderAmount: values?.minOrderAmount,
            }),
            ...(values.maxOrderAmount && {
              maxOrderAmount: values?.maxOrderAmount,
            }),
          })
        ).unwrap();
      }
      if (values?.type === PushNotificationType.orderCount) {
        await dispatch(
          createPushNotificationManagement({
            type: values.type,
            countOnly: true,
            ...(values.minOrderCount && {
              minOrderCount: values?.minOrderCount,
            }),
            ...(values.maxOrderCount && {
              maxOrderCount: values?.maxOrderCount,
            }),
          })
        ).unwrap();
      }
      if (values?.type === PushNotificationType.allUsers) {
        await dispatch(
          createPushNotificationManagement({
            type: values.type,
            countOnly: true,
          })
        ).unwrap();
      }
    }
  };

  // Category Data
  const {
    data: categoryData,
    isLoading: isCategoryLoading,
    skip: categorySkip,
    hasMore: categoryHasMore,
    status: categoryStatus,
  } = useAppSelector((state) => state.selectCategory);

  useEffect(() => {
    if (categoryStatus === 'idle') {
      dispatch(
        findSelectCategory({
          limit: 10,
          skip: categorySkip,
        })
      );
    }
  }, [categorySkip, categoryStatus]);

  const onScrollFetchCategory = (
    e: React.UIEvent<HTMLUListElement, UIEvent>,
    search: string
  ) => {
    if (
      e.currentTarget.scrollTop + 300 >= e.currentTarget.scrollHeight &&
      categoryHasMore
    ) {
      dispatch(setSkipCategory({ skip: categorySkip + 10 }));
      dispatch(
        findSelectCategory({
          limit: 10,
          skip: categorySkip + 10,
          ...(search && { search: search }),
        })
      );
    }
  };

  const searchCategory = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetCategory());
      return;
    }
    const params = {
      ...(!e.target.value && { limit: 10 }),
      ...(!e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipCategory({ skip: 0 }));
    dispatch(searchSelectCategory(params));
  };

  const uniqueCategoryData = uniqBy([...categoryData], 'id');

  const handleAddChip = (chip: string) => {
    if (chip && !chips.includes(chip)) {
      setChips([...chips, chip.trim()]);
      setInputValue('');
    }
  };

  useEffect(() => {
    if (isValidArray(chips)) {
      setFieldValue('zone', chips);
    }
  }, [chips]);

  return (
    <Dialog
      fullWidth
      open={toggle}
      TransitionComponent={Transition}
      keepMounted
      onClose={() => {
        resetForm();
        dispatch(resetPushNotificationManagement());
        onClose();
      }}
      aria-describedby='alert-dialog-slide-description'
    >
      <DialogTitle sx={{ mt: -0.5, mb: -0.5 }}>Notification</DialogTitle>
      <form
        onSubmit={handleSubmit}
        autoComplete='off'
      >
        <DialogContent dividers>
          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={12}
              sm={12}
            >
              <FormControl
                fullWidth
                error={Boolean(touched?.type && errors?.type)}
              >
                <InputLabel>{'Type'}</InputLabel>
                <Select
                  name='type'
                  labelId='type'
                  id='type'
                  value={values.type}
                  label={'Type'}
                  onChange={(e) => setFieldValue('type', e.target.value)}
                  MenuProps={{
                    style: {
                      maxHeight: 400,
                    },
                  }}
                >
                  {PUSH_NOTIFICATION_TYPE?.map((type) => (
                    <MenuItem
                      key={type.value}
                      value={type.value}
                    >
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
                {Boolean(touched.type && errors.type) && (
                  <FormHelperText>{errors?.type}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
            >
              <TextField
                id='title'
                name='title'
                label='Title'
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.title}
                error={Boolean(errors.title && touched.title)}
                helperText={
                  Boolean(errors.title && touched.title) && errors.title
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
            >
              <TextField
                id='message'
                name='message'
                label='Message'
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.message}
                error={Boolean(errors.message && touched.message)}
                helperText={
                  Boolean(errors.message && touched.message) && errors.message
                }
              />
            </Grid>
            {values?.type === PushNotificationType.address && (
              <Grid
                item
                xs={12}
                sm={12}
              >
                <IAutocomplete
                  freeSolo
                  multiple
                  options={[]}
                  value={chips}
                  onChange={(event, newValue) => {
                    setChips(newValue);
                  }}
                  inputValue={inputValue}
                  onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                  }}
                  renderTags={(value: string[], getTagProps) =>
                    value.map((option: string, index: number) => (
                      // eslint-disable-next-line react/jsx-key
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        onDelete={undefined}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant='outlined'
                      label='Enter Multiple Zones'
                      helperText={'Press enter after each zone number.'}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleAddChip(inputValue);
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        onBlur: () => {
                          handleAddChip(inputValue);
                        },
                      }}
                    />
                  )}
                />
                <Typography
                  variant='caption'
                  gutterBottom
                  // sx={{ display: 'block' }}
                ></Typography>
                {/* <TextField
                  id='zone'
                  name='zone'
                  label='Zone'
                  fullWidth
                  variant='outlined'
                  onChange={handleChange}
                  value={values.zone}
                  error={Boolean(errors.zone && touched.zone)}
                  helperText={
                    Boolean(errors.zone && touched.zone) && errors.zone
                  }
                /> */}
              </Grid>
            )}
            {values?.type === PushNotificationType.category && (
              <Grid
                item
                xs={12}
                sm={12}
              >
                <AutoComplete
                  key={`toC-${values?.categoryId}`}
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                    } else if (newValue) {
                      setFieldValue('categoryId', newValue.id);
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (!newInputValue) {
                      setFieldValue('categoryId', '');
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>{option.labelEn}</li>
                  )}
                  getOptionLabel={(option) => option.labelEn}
                  error={Boolean(touched.categoryId && errors.categoryId)}
                  helperText={
                    Boolean(touched.categoryId && errors.categoryId)
                      ? errors.categoryId
                      : ''
                  }
                  fetchFunction={onScrollFetchCategory}
                  label={'Select Occasion'}
                  loading={isCategoryLoading}
                  options={uniqueCategoryData}
                  searchFunction={searchCategory}
                  value={
                    uniqueCategoryData?.filter(
                      (category) => category.id === values.categoryId
                    )?.[0] || null
                  }
                />
              </Grid>
            )}
            {values?.type === PushNotificationType.orderAmount && (
              <>
                <Grid
                  item
                  xs={12}
                  sm={12}
                >
                  <TextField
                    id='minOrderAmount'
                    name='minOrderAmount'
                    label={'Min Order Amount'}
                    fullWidth
                    variant='outlined'
                    onChange={handleChange}
                    value={values.minOrderAmount}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    error={Boolean(
                      errors.minOrderAmount && touched.minOrderAmount
                    )}
                    helperText={
                      Boolean(
                        errors.minOrderAmount && touched.minOrderAmount
                      ) && errors.minOrderAmount
                    }
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                >
                  <TextField
                    id='maxOrderAmount'
                    name='maxOrderAmount'
                    label={'Max Order Amount'}
                    fullWidth
                    variant='outlined'
                    onChange={handleChange}
                    value={values.maxOrderAmount}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    error={Boolean(
                      errors.maxOrderAmount && touched.maxOrderAmount
                    )}
                    helperText={
                      Boolean(
                        errors.maxOrderAmount && touched.maxOrderAmount
                      ) && errors.maxOrderAmount
                    }
                  />
                </Grid>
              </>
            )}
            {values?.type === PushNotificationType.orderCount && (
              <>
                <Grid
                  item
                  xs={12}
                  sm={12}
                >
                  <TextField
                    id='minOrderCount'
                    name='minOrderCount'
                    label={'Min Order Count'}
                    fullWidth
                    variant='outlined'
                    onChange={handleChange}
                    value={values.minOrderCount}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    error={Boolean(
                      errors.minOrderCount && touched.minOrderCount
                    )}
                    helperText={
                      Boolean(errors.minOrderCount && touched.minOrderCount) &&
                      errors.minOrderCount
                    }
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                >
                  <TextField
                    id='maxOrderCount'
                    name='maxOrderCount'
                    label={'Max Order Count'}
                    fullWidth
                    variant='outlined'
                    onChange={handleChange}
                    value={values.maxOrderCount}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    error={Boolean(
                      errors.maxOrderCount && touched.maxOrderCount
                    )}
                    helperText={
                      Boolean(errors.maxOrderCount && touched.maxOrderCount) &&
                      errors.maxOrderCount
                    }
                  />
                </Grid>
              </>
            )}
            {values?.type === PushNotificationType.language && (
              <Grid
                item
                xs={12}
                sm={12}
              >
                <FormControl
                  fullWidth
                  error={Boolean(touched?.language && errors?.language)}
                >
                  <InputLabel>{'Language'}</InputLabel>
                  <Select
                    name='language'
                    labelId='language'
                    id='language'
                    value={values.language}
                    label={'Language'}
                    onChange={(e) => setFieldValue('language', e.target.value)}
                    MenuProps={{
                      style: {
                        maxHeight: 400,
                      },
                    }}
                  >
                    {LANGUAGE?.map((type) => (
                      <MenuItem
                        key={type.value}
                        value={type.value}
                      >
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {Boolean(touched.language && errors.language) && (
                    <FormHelperText>{errors?.language}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            )}
            {values?.type === PushNotificationType.allDownloadedUsers && (
              <>
                <Grid
                  item
                  xs={12}
                  sm={12}
                >
                  <FormControl
                    fullWidth
                    error={Boolean(touched?.language && errors?.language)}
                  >
                    <InputLabel>{'Language'}</InputLabel>
                    <Select
                      name='language'
                      labelId='language'
                      id='language'
                      value={values.language}
                      label={'Language'}
                      onChange={(e) => setFieldValue('language', e.target.value)}
                      MenuProps={{
                        style: {
                          maxHeight: 400,
                        },
                      }}
                    >
                      {LANGUAGE?.map((type) => (
                        <MenuItem
                          key={type.value}
                          value={type.value}
                        >
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {Boolean(touched.language && errors.language) && (
                      <FormHelperText>{errors?.language}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                >
                  <FormControl
                    fullWidth
                    error={Boolean(touched?.notificationType && errors?.notificationType)}
                  >
                    <InputLabel>{'Notification Type'}</InputLabel>
                    <Select
                      name='notificationType'
                      labelId='notificationType'
                      id='notificationType'
                      value={values.notificationType || 'immediate'}
                      label={'Notification Type'}
                      onChange={(e) => {
                        setFieldValue('notificationType', e.target.value);
                        if (e.target.value === 'immediate') {
                          setFieldValue('scheduledAt', '');
                        }
                      }}
                    >
                      <MenuItem value='immediate'>Immediate</MenuItem>
                      <MenuItem value='scheduled'>Scheduled</MenuItem>
                    </Select>
                    {Boolean(touched.notificationType && errors.notificationType) && (
                      <FormHelperText>{errors?.notificationType}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                {values?.notificationType === 'scheduled' && (
                  <Grid
                    item
                    xs={12}
                    sm={12}
                  >
                    <TextField
                      fullWidth
                      type='datetime-local'
                      label='Scheduled Date & Time (Qatar Time)'
                      name='scheduledAt'
                      value={values.scheduledAt || ''}
                      onChange={handleChange}
                      error={Boolean(touched.scheduledAt && errors.scheduledAt)}
                      helperText={touched.scheduledAt && errors.scheduledAt}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        min: getQatarDatetimeLocalMin(),
                      }}
                    />
                  </Grid>
                )}
              </>
            )}
            {count !== 0 && values?.type !== PushNotificationType.allDownloadedUsers && (
              <Grid
                item
                xs={12}
                sm={12}
              >
                Number of users: {count}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          {values?.type !== PushNotificationType.allDownloadedUsers && (
            <Button
              onClick={handleGetCountUser}
              variant='contained'
            >
              {'Calculate users'}
            </Button>
          )}
          <Button
            onClick={() => {
              resetForm();
              dispatch(resetPushNotificationManagement());
              onClose();
            }}
            variant='outlined'
          >
            {'Cancel'}
          </Button>
          <LoadingButton
            type='submit'
            loading={isSubmitting}
            variant='contained'
          >
            {'Save'}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default NotificationFormModal;